import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Grid,
  Stack
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon, School as SchoolIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from '../utils/axios';
import { Link as RouterLink } from 'react-router-dom';

const ReligiousEducationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addRecipientDialog, setAddRecipientDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');

  const deepCompare = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  const hasChanges = !deepCompare(settings, originalSettings);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/religious-education/settings');
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      setError('Error fetching settings');
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    let current = obj;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
    });
  };

  const handleSettingsChange = (field, value) => {
    const updatedSettings = JSON.parse(JSON.stringify(settings));
    setNestedValue(updatedSettings, field, value);
    setSettings(updatedSettings);
    setSuccess(null);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) {
      setSuccess('No changes to save.');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.put('/religious-education/settings', settings);
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
      setSuccess('Settings saved successfully!');
    } catch (error) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    setError(null);
    setSuccess(null);
  };

  const handleAddRecipient = async () => {
    const updatedSettings = JSON.parse(JSON.stringify(settings));
    updatedSettings.emailNotifications.newRegistration.recipients = [
      ...(updatedSettings.emailNotifications.newRegistration.recipients || []),
      newRecipient
    ];
    setSettings(updatedSettings);
    setAddRecipientDialog(false);
    setNewRecipient('');
  };

  const handleRemoveRecipient = async (emailToRemove) => {
    const updatedSettings = JSON.parse(JSON.stringify(settings));
    updatedSettings.emailNotifications.newRegistration.recipients =
      (updatedSettings.emailNotifications.newRegistration.recipients || []).filter(r => r !== emailToRemove);
    setSettings(updatedSettings);
  };

  if (loading) {
    return <Container sx={{mt: 2}}><Typography>Loading settings...</Typography></Container>;
  }

  if (error && !settings) {
    return (
        <Container sx={{mt: 2}}>
            <Alert severity="error">{error} <Button onClick={fetchSettings}>Try Again</Button></Alert>
        </Container>
    );
  }

  if (!settings || !settings.discount || !settings.emailNotifications || !settings.emailNotifications.newRegistration) {
    return <Container sx={{mt:2}}><Typography>Loading settings data or settings structure is incomplete...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5">
            Religious Education Settings
            </Typography>
            <Button 
                variant="outlined" 
                component={RouterLink} 
                to="/religious-education"
                startIcon={<ArrowBackIcon />}
            >
                Back to Registrations
            </Button>
        </Stack>

        {/* School Year Management */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            School Year Management
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Current School Year"
                value={settings.currentSchoolYear || ''}
                onChange={(e) => handleSettingsChange('currentSchoolYear', e.target.value)}
                SelectProps={{
                  native: true,
                }}
                helperText="New registrations will be assigned to this year"
              >
                <option value="">Select a school year</option>
                {(settings.availableSchoolYears || []).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                startIcon={<SchoolIcon />}
                component={RouterLink}
                to="/religious-education/programs"
                fullWidth
                sx={{ height: '56px' }}
              >
                Manage Programs
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Registration Status */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Registration Status
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.registrationEnabled}
                onChange={(e) => handleSettingsChange('registrationEnabled', e.target.checked)}
              />
            }
            label="Enable Registrations"
          />
        </Box>

        {/* Program Fees */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Program Fees
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Registration Fee ($)"
                type="number"
                value={settings.registrationFee || 0}
                onChange={(e) => handleSettingsChange('registrationFee', Number(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Additional Child Fee ($)"
                type="number"
                value={settings.additionalChildFee || 0}
                onChange={(e) => handleSettingsChange('additionalChildFee', Number(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Sacramental Preparation Fee ($)"
                type="number"
                value={settings.sacramentalPreparationFee || 0}
                onChange={(e) => handleSettingsChange('sacramentalPreparationFee', Number(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Discount Program */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Discount Program
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.discount.enabled}
                    onChange={(e) => handleSettingsChange('discount.enabled', e.target.checked)}
                  />
                }
                label="Enable Discount Program"
              />
            </Grid>
            {settings.discount.enabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Discount Valid Until Date"
                      value={settings.discount.endDate ? new Date(settings.discount.endDate) : null}
                      onChange={(date) => handleSettingsChange('discount.endDate', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discounted Registration Fee ($)"
                    type="number"
                    value={settings.discount.discountedRegistrationFee || 0}
                    onChange={(e) => handleSettingsChange('discount.discountedRegistrationFee', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discounted Additional Child Fee ($)"
                    type="number"
                    value={settings.discount.discountedAdditionalChildFee || 0}
                    onChange={(e) => handleSettingsChange('discount.discountedAdditionalChildFee', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Email Notifications */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Email Notifications (New Registration)
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddRecipientDialog(true)}
            sx={{ mb: 2 }}
          >
            Add Recipient
          </Button>
          <List>
            {settings.emailNotifications.newRegistration.recipients.map((recipient) => (
              <ListItem key={recipient}>
                <ListItemText
                  primary={recipient}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveRecipient(recipient)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button 
              variant="outlined"
              onClick={handleCancelChanges}
              disabled={!hasChanges || isSaving}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              startIcon={<SaveIcon />}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Add Recipient Dialog */}
      <Dialog open={addRecipientDialog} onClose={() => setAddRecipientDialog(false)}>
        <DialogTitle>Add Email Recipient</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRecipientDialog(false)}>Cancel</Button>
          <Button onClick={handleAddRecipient} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReligiousEducationSettings; 